<script setup>
import { computed, ref, nextTick, onMounted, watch } from 'vue'
import { useUserStore } from '@/stores/user'
import { useMessagingStore } from '@/stores/messaging'
import { useChatStore } from '@/stores/chat'
import { groupReportsByDay } from '@/utils/datetime'
import ChatAvatar from '@/pages/Project/Chat/ChatAvatar.vue'
import ChatMessage from '@/pages/Project/Chat/ChatMessage.vue'
import ChatComposer from '@/pages/Project/Chat/ChatComposer.vue'

const userStore = useUserStore()

const messagingStore = useMessagingStore()

const chatStore = useChatStore()

const conversation = computed(() => messagingStore.activeConversation)
const conversationKey = computed(() => conversation.value?.key)
const peer = computed(() => conversation.value?.peer || {})
const isAgent = computed(() => conversation.value?.kind === 'agent')
const tz = computed(() => userStore.settings?.tz)

const messages = computed(() =>
  conversationKey.value ? messagingStore.messages(conversationKey.value) : []
)
const pendingAgent = computed(() =>
  conversationKey.value ? messagingStore.pendingAgent(conversationKey.value) : null
)

const groups = computed(() =>
  groupReportsByDay(messages.value, tz.value, (m) => m.createdAt)
)

const feedEl = ref(null)
const composerEl = ref(null)
const dragging = ref(false)

function scrollToBottom(behavior = 'auto') {
  const el = feedEl.value
  if (!el) return
  el.scrollTo({ top: el.scrollHeight, behavior })
}

function onDragOver(event) {
  if (Array.from(event.dataTransfer?.types || []).includes('Files')) dragging.value = true
}

function onDragLeave(event) {
  if (!event.currentTarget.contains(event.relatedTarget)) dragging.value = false
}

function onDrop(event) {
  dragging.value = false
  const files = Array.from(event.dataTransfer?.files || [])
  if (files.length) composerEl.value?.addFiles(files)
}

async function handleSend({ text, attachments }) {
  const key = conversationKey.value
  if (!key) return
  await messagingStore.sendMessage({ key, text, attachments })
  await nextTick()
  scrollToBottom('smooth')

  // The server auto-replies in agent DMs; just show the typing indicator
  // until the reply arrives over WS.
  if (isAgent.value) messagingStore.notePendingAgent({ key })
}

watch(conversationKey, async () => {
  await nextTick()
  scrollToBottom()
})

// Live arrivals keep the feed pinned and the read watermark current.
watch(
  () => messages.value.length,
  async () => {
    await nextTick()
    scrollToBottom('smooth')
    if (conversationKey.value) chatStore.markRead(conversationKey.value)
  }
)

// The typing indicator renders below the last message — bring it into view.
watch(pendingAgent, async (name) => {
  if (!name) return
  await nextTick()
  scrollToBottom('smooth')
})

onMounted(async () => {
  await nextTick()
  scrollToBottom()
})
</script>

<template>
  <div v-if="conversation" class="flex h-full min-h-0 flex-col">
    <!-- The panel header shows the peer name and the back button; the avatar
         travels up beside the centered title. -->
    <Teleport to="#panel-title-leading" defer>
      <ChatAvatar :author="peer" size="base" />
    </Teleport>

    <!-- Feed -->
    <div
      ref="feedEl"
      class="min-h-0 flex-1 overflow-y-auto px-2 py-2"
      @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent.stop="onDrop"
    >
      <div
        v-if="!messages.length"
        class="rounded-lg border bg-card px-4 py-8 text-center text-sm text-muted-foreground"
      >
        <ChatAvatar :author="peer" size="base" class="mx-auto mb-2" />
        {{ isAgent ? `Ask ${peer.display_name} anything` : `Start a conversation with ${peer.display_name}` }}
      </div>

      <section v-for="group in groups" :key="group.key" class="mb-2">
        <div class="relative my-3 text-center">
          <div class="absolute inset-x-0 top-1/2 border-t border-border/60"></div>
          <span class="relative z-10 bg-background px-2 text-xs font-medium text-muted-foreground">
            {{ group.label }}
          </span>
        </div>
        <div class="space-y-4">
          <ChatMessage
            v-for="message in group.items"
            :key="message.id"
            :message="message"
            :show-replies="false"
          />
        </div>
      </section>

      <div v-if="pendingAgent" class="flex items-center gap-2 px-1 py-2 text-sm text-muted-foreground">
        <span class="flex gap-1">
          <span class="size-1.5 animate-bounce rounded-full bg-muted-foreground/60"></span>
          <span class="size-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:150ms]"></span>
          <span class="size-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:300ms]"></span>
        </span>
        {{ pendingAgent }} is thinking…
      </div>
    </div>

    <!-- Composer -->
    <div
      class="border-t bg-background px-2 py-2"
      @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent.stop="onDrop"
    >
      <div
        v-if="dragging"
        class="mb-1.5 rounded-lg border border-dashed border-primary/60 bg-primary/5 px-2.5 py-1.5 text-center text-xs font-medium text-primary"
      >
        Drop images to attach
      </div>
      <ChatComposer
        ref="composerEl"
        :data-attachments="false"
        :placeholder="`Message ${peer.display_name}`"
        @send="handleSend"
      />
    </div>
  </div>
</template>
